<script setup lang="ts">
import * as THREE from 'three'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import {
  CameraMode,
  type CharacterLookInteraction,
  createSpatialCapsuleWalkPlugins,
  type GaussianSplatPlugin,
  SpatialCapsuleClickToWalkController,
  type SpatialBindingChangedEventPayload,
  type SpatialCapsuleClickToWalkSnapshot,
  type SpatialBindingSnapshot,
  type SpatialRepresentationErrorEventPayload,
  type SpatialRepresentationEventPayload,
  type SpatialRepresentationRole,
  type SpatialRepresentationState,
  type SpatialWorldHandle,
} from '@sharefe/share3d-engine'
import { getDefaultPresetModelUrl, PRESET_MODELS } from '../examples/presetModels'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import { use3DGS } from '../hooks/use3DGS'
import {
  createConfiguredGsplatPlugin,
  getGsplatGraphicsConfig,
  resolveGsplatBackendControlState,
} from '../utils/gsplatBackendControl'
import { resolveSpatialWorldInputPath } from '../utils/assetPath'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import GSplatBackendControlCard from './GSplatBackendControlCard.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneGroupCard from './SceneGroupCard.vue'
import SceneSpatialQueryCard from './SceneSpatialQueryCard.vue'
import SceneStatusCard from './SceneStatusCard.vue'
import {
  SpatialCapsuleWalkRuntime,
  type SpatialCapsuleWalkMode,
  type SpatialCapsuleWalkRuntimeState,
} from '../spatialCapsuleWalk/SpatialCapsuleWalkRuntime'

type SpatialEventItem = {
  id: string
  message: string
}

// 默认加载预设 model1
const DEFAULT_GS3D_MODEL = getDefaultPresetModelUrl()

const SPATIAL_ROLES: SpatialRepresentationRole[] = [
  'surface',
  'collision',
  'navigation',
]

const containerRef = useTemplateRef('containerRef')
const backendControlState = resolveGsplatBackendControlState(window.location.hash, {
  webgpuSupported: true,
})

const graphicsCard = useExampleGraphicsCard({
  presetKey: 'pointCloudAndGSplat',
  extras: ['CharacterPlugin'],
})
const clickToWalkController = new SpatialCapsuleClickToWalkController()
const {
  engine,
  isLoading,
  modelInput,
  stats,
  loadedHandles,
  init,
  loadModel,
  removeAllModels,
  autoLoad,
} = use3DGS(containerRef, {
  plugins: [
    ...createSpatialCapsuleWalkPlugins({
      assetPlugins: [createConfiguredGsplatPlugin(backendControlState)],
      onModelStateChange: (state, error) => {
        if (state !== 'error') {
          return
        }

        appendLog(`TimmyRobot 模型加载失败，已使用胶囊体回退：${error?.message ?? '未知错误'}`)
      },
    }),
    clickToWalkController,
  ],
  defaultModelUrl: DEFAULT_GS3D_MODEL,
  autoLoadFromUrl: true,
  graphics: getGsplatGraphicsConfig(backendControlState),
  renderMode: 'on-demand',
  fov: 65,
  background: '#030712',
})

const proxyInput = ref('')
const loadError = ref('')
const worldState = ref<'idle' | 'building' | 'ready' | 'error'>('idle')
const worldId = ref('')
const bindingId = ref('')
const debugVisible = ref(false)
const bvhVisible = ref(false)
const bvhDepth = ref(10)
const gsVisible = ref(true)
const eventLog = ref<SpatialEventItem[]>([])
const runtime = shallowRef<SpatialCapsuleWalkRuntime | null>(null)
const runtimeState = ref<SpatialCapsuleWalkRuntimeState>(createEmptyRuntimeState())

let currentWorldHandle: SpatialWorldHandle | null = null
const currentBindings = ref<SpatialBindingSnapshot[]>([])
const cleanupTasks: Array<() => void> = []

const displayWorldState = computed(() => formatWorldStateLabel(worldState.value))
const spatialBadge = computed(() => {
  if (!currentWorldHandle) {
    return 'Spatial FPS'
  }

  return displayWorldState.value
})
const canClearSpatial = computed(() => {
  return !!currentWorldHandle || currentBindings.value.length > 0
})
const canControlCharacter = computed(() => {
  return worldState.value === 'ready' && !!runtime.value
})
const runtimeBadge = computed(() => {
  if (!canControlCharacter.value) {
    return '等待就绪'
  }

  if (runtimeState.value.mode === 'third-person') {
    return '第三人称'
  }

  if (runtimeState.value.lookInteraction === 'drag-look') {
    return '第一人称 / Drag Look'
  }

  return runtimeState.value.pointerLocked ?
      '第一人称 / Pointer Lock' :
      '第一人称 / 待锁定'
})
const panelMeta = computed(() => {
  if (!canControlCharacter.value) {
    return '等待世界就绪'
  }

  return `${formatRuntimeModeLabel(runtimeState.value.mode)} / ${formatLookInteractionLabel(runtimeState.value.mode, runtimeState.value.lookInteraction)}`
})
const modelLoaderHint = computed(() => {
  if (loadError.value) {
    return `加载失败：${loadError.value}`
  }

  return '先通过目录选择或 URL 参数加载 3DGS，再绑定 collision proxy。示例会用同一个 spatial world 驱动角色运行时、第三人称 observer 和投掷球碰撞。'
})
const statusItems = computed(() => {
  return [
    { label: '空间世界', value: displayWorldState.value },
    { label: '镜头模式', value: formatRuntimeModeLabel(runtimeState.value.mode) },
    {
      label: '视角输入',
      value: formatLookInteractionLabel(
        runtimeState.value.mode,
        runtimeState.value.lookInteraction,
      ),
    },
    { label: '角色状态', value: runtimeState.value.grounded ? '地面' : '空中' },
    { label: '移动速度', value: `${runtimeState.value.speed.toFixed(2)} m/s` },
    { label: '当前台阶', value: `${runtimeState.value.stepHeight.toFixed(2)} m` },
    { label: '自动行走', value: clickToWalkStatus.value },
    { label: '活动球数', value: runtimeState.value.activeBalls },
    {
      label: '观察视图',
      value: runtimeState.value.observerViewVisible ?
          runtimeState.value.observerViewId ?? '已启用' :
          '关闭',
    },
    { label: '已绑定对象', value: currentBindings.value.length },
    { label: '已加载 3DGS', value: stats.value.totalModels },
  ]
})
const playerSummary = computed(() => {
  return formatVector(runtimeState.value.position)
})
const velocitySummary = computed(() => {
  return formatVector(runtimeState.value.velocity)
})
const runtimeTip = computed(() => {
  if (!canControlCharacter.value) {
    return '先让 spatial world 进入就绪状态，再开启角色运行时。'
  }

  if (runtimeState.value.mode === 'third-person') {
    return 'WASD 相对相机移动，Space 跳跃，Shift 冲刺，单击可行走位置自动行走，按住左键环绕。'
  }

  if (runtimeState.value.lookInteraction === 'drag-look') {
    return '按住左键拖动视角，WASD 移动，轻点可行走位置自动行走，按 F 或按钮投球。'
  }

  return runtimeState.value.pointerLocked ?
      'WASD 移动，Space 跳跃，Shift 冲刺，左键或 F 投球，Esc 退出 Pointer Lock。' :
      '点击“第一人称 / Pointer Lock”后会尝试锁定鼠标；若浏览器未锁定，可再点一次视口。'
})
const clickToWalkStatus = computed(() => {
  return formatClickToWalkStatus(runtimeState.value.clickToWalk)
})

function appendLog(message: string) {
  eventLog.value = [
    {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      message,
    },
    ...eventLog.value,
  ].slice(0, 10)
}

function deriveWorldState(
  states: SpatialRepresentationState[],
): 'idle' | 'building' | 'ready' | 'error' {
  if (states.length === 0 || states.every((state) => state === 'absent')) {
    return 'idle'
  }

  if (states.some((state) => state === 'error')) {
    return 'error'
  }

  if (states.some((state) => state === 'building')) {
    return 'building'
  }

  if (states.some((state) => state === 'ready')) {
    return 'ready'
  }

  return 'idle'
}

function syncBindingState() {
  const engineInstance = engine.value
  const activeWorldId = currentWorldHandle?.id
  if (!engineInstance || !activeWorldId) {
    currentBindings.value = []
    bindingId.value = ''
    return
  }

  currentBindings.value = engineInstance.spatial.getBindings({
    worldId: activeWorldId,
    includeInactive: true,
  })
  bindingId.value = currentBindings.value.map((binding) => binding.id).join(', ')
}

function syncGaussianVisibility() {
  for (const handle of loadedHandles.value) {
    if (handle.object3D) {
      handle.object3D.visible = gsVisible.value
    }
  }
}

function applySpatialDebugState() {
  const engineInstance = engine.value
  const activeWorldId = currentWorldHandle?.id
  if (!engineInstance || !activeWorldId) {
    return
  }

  for (const role of SPATIAL_ROLES) {
    engineInstance.spatial.setDebugMeshVisible(activeWorldId, role, debugVisible.value)
    engineInstance.spatial.setDebugBoundsVisible(activeWorldId, role, bvhVisible.value)
    engineInstance.spatial.setDebugBoundsDepth(activeWorldId, role, bvhDepth.value)
  }
}

function clearRuntime() {
  runtime.value?.dispose()
  runtime.value = null
  runtimeState.value = createEmptyRuntimeState()
}

function syncSpatialState() {
  if (!currentWorldHandle) {
    worldState.value = 'idle'
    worldId.value = ''
    syncBindingState()
    return
  }

  try {
    const snapshot = currentWorldHandle.snapshot()
    worldState.value = deriveWorldState(
      SPATIAL_ROLES.map((role) => snapshot.representations[role].state),
    )
    worldId.value = snapshot.id
  } catch {
    currentWorldHandle = null
    worldState.value = 'idle'
    worldId.value = ''
  }

  syncBindingState()
}

function teardownBindings() {
  while (cleanupTasks.length > 0) {
    cleanupTasks.pop()?.()
  }
}

function setInitialLoadedCameraView() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  engineInstance.camera.setState({
    ...engineInstance.camera.getState(),
    mode: CameraMode.Perspective,
    position: new THREE.Vector3(-4, -4, 2.4),
    target: new THREE.Vector3(0, 0, 1.4),
    up: new THREE.Vector3(0, 0, 1),
  })
}

function resolveSpawnFloorPoint(): THREE.Vector3 {
  return new THREE.Vector3(0, 0, 0)
}

function ensureRuntime(reset = false) {
  const engineInstance = engine.value
  const containerElement = containerRef.value
  if (
    !engineInstance ||
    !containerElement ||
    !currentWorldHandle ||
    worldState.value !== 'ready'
  ) {
    return
  }

  const spawnFloorPoint = resolveSpawnFloorPoint()
  if (!runtime.value) {
    runtime.value = new SpatialCapsuleWalkRuntime({
      engine: engineInstance,
      container: containerElement,
      worldId: currentWorldHandle.id,
      spawnFloorPoint,
      defaultMode: 'third-person',
      defaultLookInteraction: 'pointer-lock',
      observerViewVisible: false,
      clickToWalk: clickToWalkController,
      onStateChange: (state) => {
        runtimeState.value = {
          ...state,
          position: state.position.clone(),
          velocity: state.velocity.clone(),
          clickToWalk: cloneClickToWalkSnapshot(state.clickToWalk),
        }
      },
    })
    appendLog('角色运行时已就绪')
    return
  }

  runtime.value.setWorldId(currentWorldHandle.id)
  runtime.value.setSpawnFloorPoint(spawnFloorPoint, reset)
}

function bindEngineEvents() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  teardownBindings()

  const onSpatialRepresentationReady = (
    event: SpatialRepresentationEventPayload,
  ) => {
    if (event.worldId !== currentWorldHandle?.id) {
      return
    }

    syncSpatialState()
    applySpatialDebugState()
    appendLog(`表征已就绪：${formatRepresentationRoleLabel(event.representationRole)}`)

    if (worldState.value === 'ready') {
      ensureRuntime(true)
    }
  }

  const onSpatialRepresentationError = (
    event: SpatialRepresentationErrorEventPayload,
  ) => {
    if (event.worldId !== currentWorldHandle?.id) {
      return
    }

    syncSpatialState()
    loadError.value = event.error.message
    appendLog(`表征构建失败：${formatRepresentationRoleLabel(event.representationRole)}`)
  }

  const onSpatialDisposed = (event: { worldId: string }) => {
    if (event.worldId !== currentWorldHandle?.id) {
      return
    }

    clearRuntime()
    currentWorldHandle = null
    currentBindings.value = []
    bindingId.value = ''
    syncSpatialState()
    appendLog(`空间世界已释放：${event.worldId}`)
  }

  const onSpatialBindingChanged = (
    event: SpatialBindingChangedEventPayload,
  ) => {
    if (event.binding.worldId !== currentWorldHandle?.id) {
      return
    }

    syncBindingState()
    appendLog(`${formatBindingActionLabel(event.action)}：${event.binding.id}`)
  }

  engineInstance.events.on('spatial.representationReady', onSpatialRepresentationReady)
  engineInstance.events.on('spatial.representationError', onSpatialRepresentationError)
  engineInstance.events.on('spatial.worldDisposed', onSpatialDisposed)
  engineInstance.events.on('spatial.bindingChanged', onSpatialBindingChanged)

  cleanupTasks.push(() =>
    engineInstance.events.off('spatial.representationReady', onSpatialRepresentationReady),
  )
  cleanupTasks.push(() =>
    engineInstance.events.off('spatial.representationError', onSpatialRepresentationError),
  )
  cleanupTasks.push(() =>
    engineInstance.events.off('spatial.worldDisposed', onSpatialDisposed),
  )
  cleanupTasks.push(() =>
    engineInstance.events.off('spatial.bindingChanged', onSpatialBindingChanged),
  )
}

function clearProxyBinding() {
  clearRuntime()
  currentBindings.value = []
  bindingId.value = ''

  if (currentWorldHandle) {
    currentWorldHandle.dispose()
    currentWorldHandle = null
  }

  syncSpatialState()
}

function clearScene() {
  clearProxyBinding()

  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  for (const handle of plugin?.getAll() ?? []) {
    plugin?.unmount(handle.id)
    plugin?.dispose(handle.id)
  }

  removeAllModels(false)
  loadError.value = ''
  appendLog('场景已清空')
}

async function handleLoadModel() {
  loadError.value = ''

  try {
    if (!modelInput.value.trim()) {
      loadError.value = '请先选择 3DGS 目录，或通过 URL 的 models 参数传入模型入口。'
      return
    }

    if (stats.value.totalModels > 0) {
      clearScene()
    }

    await loadModel()
    setInitialLoadedCameraView()
    syncGaussianVisibility()
    appendLog('3DGS 加载完成')
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '未知 3DGS 加载错误'
  }
}

async function handleLoadProxyMesh() {
  loadError.value = ''

  const engineInstance = engine.value
  const owners = loadedHandles.value
  const proxySource = resolveSpatialWorldInputPath(proxyInput.value.trim())

  if (!engineInstance || owners.length === 0) {
    loadError.value = '请先加载至少一个 3DGS，再绑定 collision proxy。'
    return
  }

  if (!proxySource) {
    loadError.value = '请输入 proxy mesh 的 URL 或本地路径。'
    return
  }

  clearProxyBinding()

  try {
    currentWorldHandle = engineInstance.spatial.loadWorld({
      name: 'Spatial Capsule Walk World',
      representations: {
        surface: {
          source: proxySource,
          debug: {
            meshVisible: debugVisible.value,
            boundsVisible: bvhVisible.value,
            boundsDepth: bvhDepth.value,
          },
        },
        collision: {
          source: proxySource,
          debug: {
            meshVisible: debugVisible.value,
            boundsVisible: bvhVisible.value,
            boundsDepth: bvhDepth.value,
          },
        },
        navigation: {
          source: proxySource,
          debug: {
            meshVisible: debugVisible.value,
            boundsVisible: bvhVisible.value,
            boundsDepth: bvhDepth.value,
          },
        },
      },
    })

    currentBindings.value = owners.map((handle, index) =>
      engineInstance.spatial.bindWorld({
        worldId: currentWorldHandle!.id,
        ownerAssetId: handle.id,
        ownerKind: 'gaussian-splat',
        representationRoles: [...SPATIAL_ROLES],
        surfaceUsages: ['measurement', 'camera-control'],
        occlusionDepth: true,
        priority: index,
      }),
    )

    syncSpatialState()
    appendLog(`已请求构建空间世界：${currentWorldHandle.id}`)
    appendLog(`已创建绑定：${currentBindings.value.length} 个`)
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Proxy mesh 绑定失败'
  }
}

function handleEnableFirstPerson(lookInteraction: CharacterLookInteraction) {
  runtime.value?.setMode('first-person', {
    lookInteraction,
    requestPointerLock: lookInteraction === 'pointer-lock',
  })
  appendLog(
    lookInteraction === 'pointer-lock' ?
      '已切换到第一人称 Pointer Lock' :
      '已切换到第一人称 Drag Look',
  )
}

function handleEnableThirdPerson() {
  runtime.value?.setMode('third-person')
  appendLog('已切换到第三人称跟随')
}

function handleToggleObserverView() {
  const nextVisible = !runtimeState.value.observerViewVisible
  runtime.value?.setObserverViewVisible(nextVisible)
  appendLog(nextVisible ? '已开启观察视图' : '已关闭观察视图')
}

function handleResetPlayer() {
  runtime.value?.reset()
  appendLog('已重置角色出生点')
}

function handleThrowBall() {
  runtime.value?.throwBall()
  appendLog('已投出测试球')
}

function handleClearBalls() {
  runtime.value?.clearBalls()
  appendLog('已清空球池')
}

function formatWorldStateLabel(state: 'idle' | 'building' | 'ready' | 'error'): string {
  switch (state) {
    case 'idle':
      return '未创建'
    case 'building':
      return '构建中'
    case 'ready':
      return '就绪'
    case 'error':
      return '错误'
    default:
      return state
  }
}

function formatRepresentationRoleLabel(role: SpatialRepresentationRole): string {
  switch (role) {
    case 'surface':
      return '表面'
    case 'collision':
      return '碰撞'
    case 'navigation':
      return '导航'
    default:
      return role
  }
}

function formatBindingActionLabel(
  action: SpatialBindingChangedEventPayload['action'],
): string {
  switch (action) {
    case 'bound':
      return '已绑定'
    case 'updated':
      return '已更新'
    case 'unbound':
      return '已解绑'
    default:
      return action
  }
}

function formatVector(vector: THREE.Vector3): string {
  return `${vector.x.toFixed(2)}, ${vector.y.toFixed(2)}, ${vector.z.toFixed(2)}`
}

function createEmptyRuntimeState(): SpatialCapsuleWalkRuntimeState {
  return {
    mode: 'third-person',
    lookInteraction: 'pointer-lock',
    pointerLocked: false,
    grounded: false,
    speed: 0,
    activeBalls: 0,
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    stepHeight: 0,
    observerViewVisible: false,
    observerViewId: null,
    clickToWalk: createEmptyClickToWalkSnapshot(),
  }
}

function createEmptyClickToWalkSnapshot(): SpatialCapsuleClickToWalkSnapshot {
  return {
    available: false,
    active: false,
    hoverPoint: null,
    hoverNormal: null,
    hoverScreen: null,
    targetPoint: null,
    targetNormal: null,
    targetScreen: null,
    lastReason: null,
  }
}

function cloneClickToWalkSnapshot(
  snapshot: SpatialCapsuleClickToWalkSnapshot,
): SpatialCapsuleClickToWalkSnapshot {
  return {
    ...snapshot,
    hoverPoint: snapshot.hoverPoint?.clone() ?? null,
    hoverNormal: snapshot.hoverNormal?.clone() ?? null,
    hoverScreen: snapshot.hoverScreen?.clone() ?? null,
    targetPoint: snapshot.targetPoint?.clone() ?? null,
    targetNormal: snapshot.targetNormal?.clone() ?? null,
    targetScreen: snapshot.targetScreen?.clone() ?? null,
  }
}

function formatClickToWalkStatus(snapshot: SpatialCapsuleClickToWalkSnapshot): string {
  if (snapshot.active) {
    return '行走中'
  }

  if (!snapshot.available) {
    return '不可用'
  }

  if (snapshot.hoverPoint) {
    return '可点击'
  }

  return snapshot.lastReason ? formatClickToWalkReason(snapshot.lastReason) : '就绪'
}

function formatClickToWalkReason(
  reason: SpatialCapsuleClickToWalkSnapshot['lastReason'],
): string {
  switch (reason) {
    case 'arrived':
      return '已到达'
    case 'blocked':
      return '受阻'
    case 'drag':
      return '拖拽取消'
    case 'invalid-target':
      return '无效目标'
    case 'manual-input':
      return '手动接管'
    case 'mode-change':
      return '模式切换'
    case 'multi-touch':
      return '多指取消'
    case 'pointer-cancel':
      return '触摸取消'
    case 'target-set':
      return '目标已设置'
    case 'view-removed':
      return '视图失效'
    case 'world-change':
      return '世界切换'
    case 'context-lost':
    case 'disposed':
      return '已清理'
    default:
      return '就绪'
  }
}

function formatRuntimeModeLabel(mode: SpatialCapsuleWalkMode): string {
  return mode === 'first-person' ? '第一人称' : '第三人称'
}

function formatLookInteractionLabel(
  mode: SpatialCapsuleWalkMode,
  lookInteraction: CharacterLookInteraction,
): string {
  if (mode === 'third-person') {
    return 'Orbit / Follow'
  }

  return lookInteraction === 'pointer-lock' ? 'Pointer Lock' : 'Drag Look'
}

watch(debugVisible, () => {
  applySpatialDebugState()
})

watch(bvhVisible, () => {
  applySpatialDebugState()
})

watch(bvhDepth, () => {
  applySpatialDebugState()
})

watch(gsVisible, () => {
  syncGaussianVisibility()
})

watch(loadedHandles, () => {
  syncGaussianVisibility()
  syncBindingState()
})

onMounted(async () => {
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(engine.value ? engine.value.getGraphicsInfo().kind : null)
  bindEngineEvents()
  engine.value?.controls.setMode('orbit')
  await autoLoad()
  if (loadedHandles.value.length > 0) {
    setInitialLoadedCameraView()
  }
  syncGaussianVisibility()
  syncSpatialState()
})

onBeforeUnmount(() => {
  teardownBindings()
  clearProxyBinding()
})
</script>

<template>
  <div class="fps-page">
    <div class="render-shell">
      <div class="render-container" ref="containerRef"></div>

      <div class="hud-panel">
        <span class="hud-kicker">Character Runtime</span>
        <strong class="hud-title">{{ runtimeBadge }}</strong>
        <div class="hud-stats">
          <span>速度 {{ runtimeState.speed.toFixed(2) }} m/s</span>
          <span>{{ runtimeState.grounded ? '地面' : '空中' }}</span>
          <span>球 {{ runtimeState.activeBalls }}</span>
        </div>
        <p class="hud-tip">
          {{ runtimeTip }}
        </p>
      </div>

      <div v-if="runtimeState.mode === 'first-person'" class="reticle" aria-hidden="true"></div>

      <div v-if="loadError" class="error-banner">
        {{ loadError }}
      </div>
    </div>

    <SceneControlPanel
      layout="sidebar"
      kicker="Spatial Runtime"
      title="3DGS Capsule Walk"
      :meta="panelMeta"
    >
      <SceneAssetLoaderCard
        v-model="modelInput"
        title="3DGS 资源"
        subtitle="优先加载一个高斯场景，再把 proxy collision world 绑定到它。"
        badge="3DGS"
        :hint="modelLoaderHint"
        :loading="isLoading"
        :can-clear="stats.totalModels > 0"
        :directory-entry-names="['lod-meta.json']"
        :preset-models="PRESET_MODELS"
        directory-pick-label="选择 3DGS 目录"
        load-label="加载 3DGS"
        clear-label="清空场景"
        @submit-load="handleLoadModel()"
        @clear-scene="clearScene()"
      />

      <GraphicsBackendControlCard :card="graphicsCard">
        <template #extensions>
          <GSplatBackendControlCard :state="backendControlState" />
        </template>
      </GraphicsBackendControlCard>

      <SceneSpatialQueryCard
        v-model:proxy-mesh-url="proxyInput"
        v-model:debug-visible="debugVisible"
        v-model:bvh-visible="bvhVisible"
        v-model:bvh-depth="bvhDepth"
        v-model:gs-visible="gsVisible"
        title="Collision Proxy"
        subtitle="从同一个 proxy mesh 构建 surface / collision / navigation 三套表征，并驱动角色 runtime。"
        :badge="spatialBadge"
        :world-state="displayWorldState"
        :world-id="worldId"
        :binding-id="bindingId || '暂无绑定'"
        binding-duration=""
        :load-disabled="stats.totalModels === 0"
        :clear-disabled="!canClearSpatial"
        directory-pick-label="选择 proxy 目录"
        :directory-entry-names="['proxy.glb', 'proxy.gltf', 'proxy.ply', 'scene.glb', 'scene.gltf', 'scene.ply']"
        :events="eventLog"
        @load="handleLoadProxyMesh()"
        @clear="clearProxyBinding()"
      />

      <SceneGroupCard
        title="角色运行"
        subtitle="第一人称双交互、第三人称、observer view 和投掷球控制都在这里。"
        :badge="runtimeBadge"
      >
        <div class="fps-runtime-card">
          <div class="status-grid">
            <div class="status-item">
              <span class="status-label">玩家位置</span>
              <strong class="status-value">{{ playerSummary }}</strong>
            </div>
            <div class="status-item">
              <span class="status-label">速度向量</span>
              <strong class="status-value">{{ velocitySummary }}</strong>
            </div>
          </div>

          <div class="button-grid">
            <button
              class="panel-button"
              :class="{
                'panel-button-active':
                  runtimeState.mode === 'first-person' &&
                  runtimeState.lookInteraction === 'pointer-lock',
              }"
              :disabled="!canControlCharacter"
              @click="handleEnableFirstPerson('pointer-lock')"
            >
              <span class="panel-button-label">
                第一人称 / Pointer Lock
              </span>
              <span class="panel-button-meta">
                {{ runtimeState.pointerLocked ? '当前已锁定鼠标' : '点击后尝试进入锁定视角' }}
              </span>
            </button>

            <button
              class="panel-button"
              :class="{
                'panel-button-active':
                  runtimeState.mode === 'first-person' &&
                  runtimeState.lookInteraction === 'drag-look',
              }"
              :disabled="!canControlCharacter"
              @click="handleEnableFirstPerson('drag-look')"
            >
              <span class="panel-button-label">第一人称 / Drag Look</span>
              <span class="panel-button-meta">不进入锁定，按住左键拖动转向</span>
            </button>

            <button
              class="panel-button"
              :class="{ 'panel-button-active': runtimeState.mode === 'third-person' }"
              :disabled="!canControlCharacter"
              @click="handleEnableThirdPerson()"
            >
              <span class="panel-button-label">第三人称 / Follow</span>
              <span class="panel-button-meta">角色胶囊体可见，支持 orbit 和滚轮缩放</span>
            </button>

            <button
              class="panel-button panel-button-subtle"
              :class="{ 'panel-button-active': runtimeState.observerViewVisible }"
              :disabled="!canControlCharacter"
              @click="handleToggleObserverView()"
            >
              <span class="panel-button-label">
                {{ runtimeState.observerViewVisible ? '关闭观察视图' : '开启观察视图' }}
              </span>
              <span class="panel-button-meta">
                叠加一个第三人称 observer view，持续观察同一角色
              </span>
            </button>

            <button
              class="panel-button panel-button-subtle"
              :disabled="!canControlCharacter"
              @click="handleResetPlayer()"
            >
              <span class="panel-button-label">重置玩家</span>
              <span class="panel-button-meta">回到默认出生点并复用当前模式</span>
            </button>

            <button
              class="panel-button panel-button-subtle"
              :disabled="!canControlCharacter"
              @click="handleThrowBall()"
            >
              <span class="panel-button-label">手动投球</span>
              <span class="panel-button-meta">沿当前视线投出一颗测试球</span>
            </button>

            <button
              class="panel-button panel-button-subtle"
              :disabled="runtimeState.activeBalls === 0"
              @click="handleClearBalls()"
            >
              <span class="panel-button-label">清空球池</span>
              <span class="panel-button-meta">释放当前所有活动球体</span>
            </button>
          </div>

          <ul class="fps-tip-list">
            <li>`W / A / S / D`：平面移动，`Space`：跳跃，`Shift`：冲刺</li>
            <li>第三人称或 Drag Look：单击/轻触可行走位置，角色会自动走向目标</li>
            <li>第一人称 Pointer Lock：点击按钮后锁定鼠标，`Esc` 退出</li>
            <li>第一人称 Drag Look：按住左键拖动视角，不抢占鼠标</li>
            <li>第三人称：按住左键 orbit，滚轮 zoom，角色胶囊体保持可见</li>
            <li>`F` 或面板按钮：投球；observer view 可同时观察同一角色</li>
          </ul>
        </div>
      </SceneGroupCard>

      <SceneStatusCard
        title="运行状态"
        subtitle="查看世界状态、角色模式、多视图和活动球数量。"
        badge="Runtime"
        :items="statusItems"
        hint="这个示例优先验证 collision proxy 上的角色运行时体验。楼梯、斜坡、第一/第三人称切换和 observer view 都共享同一角色实例。"
      />
    </SceneControlPanel>
  </div>
</template>

<style scoped>
.fps-page {
  --panel-gap: 0px;
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
  background:
    radial-gradient(circle at top left, rgba(34, 197, 94, 0.14), transparent 28%),
    radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.12), transparent 30%),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
}

.render-shell {
  position: relative;
  min-width: 0;
  min-height: 0;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(2, 6, 23, 0.84);
}

.render-container {
  width: 100%;
  height: 100%;
  min-height: 0;
  background:
    radial-gradient(circle at top, rgba(148, 163, 184, 0.12), transparent 42%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.2), rgba(2, 6, 23, 0.9));
}

.hud-panel {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 3;
  display: grid;
  gap: 8px;
  max-width: min(420px, calc(100% - 36px));
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.74);
  backdrop-filter: blur(14px);
  color: #eff6ff;
}

.hud-kicker {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(191, 219, 254, 0.72);
}

.hud-title {
  font-size: 20px;
  font-weight: 700;
}

.hud-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: rgba(226, 232, 240, 0.86);
}

.hud-tip {
  margin: 0;
  font-size: 12px;
  line-height: 1.55;
  color: rgba(226, 232, 240, 0.74);
}

.reticle {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 18px;
  height: 18px;
  margin-left: -9px;
  margin-top: -9px;
  z-index: 4;
}

.reticle::before,
.reticle::after {
  content: '';
  position: absolute;
  background: rgba(248, 250, 252, 0.92);
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.28);
}

.reticle::before {
  left: 8px;
  top: 2px;
  width: 2px;
  height: 14px;
}

.reticle::after {
  left: 2px;
  top: 8px;
  width: 14px;
  height: 2px;
}

.error-banner {
  position: absolute;
  left: 18px;
  bottom: 18px;
  z-index: 3;
  max-width: min(520px, calc(100% - 36px));
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(248, 113, 113, 0.28);
  background: rgba(127, 29, 29, 0.72);
  color: #fee2e2;
  font-size: 13px;
  line-height: 1.55;
}

.fps-runtime-card {
  display: grid;
  gap: 12px;
}

.fps-tip-list {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
  color: rgba(226, 232, 240, 0.78);
  font-size: 12px;
  line-height: 1.5;
}

.panel-button-active {
  border-color: rgba(96, 165, 250, 0.55);
  box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.25);
  background: rgba(30, 41, 59, 0.86);
}

@media (max-width: 1080px) {
  .fps-page {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto;
  }

  .render-shell {
    min-height: 58vh;
  }
}
</style>
